import { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { DatePicker } from "@/components/ui/date-picker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { Search, User, Phone, MapPin, Calendar, CheckCircle2, XCircle, Pencil, GraduationCap, Rocket, AlertTriangle, Megaphone, Cake, RotateCcw, Smartphone } from "lucide-react";
import { toast } from "sonner";

export default function StudentSearch() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "teacher";
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);

  const onSearch = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true); setSelected(null);
    try {
      const { data } = await api.get("/students/search", { params: { q: q.trim() } });
      setResults(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Search failed");
    } finally { setLoading(false); }
  };

  const openDetail = async (s) => {
    setDetailLoading(true);
    try {
      const { data } = await api.get(`/students/${s.id}`);
      setSelected(data);
    } catch (e) {
      toast.error("Failed to load student");
    } finally { setDetailLoading(false); }
  };

  const [searchParams] = useSearchParams();
  const didDeepLink = useRef(false);
  useEffect(() => {
    const initial = searchParams.get("q");
    if (!initial || didDeepLink.current) return;
    didDeepLink.current = true;
    setQ(initial);
    (async () => {
      setLoading(true); setSelected(null);
      try {
        const { data } = await api.get("/students/search", { params: { q: initial.trim() } });
        setResults(data);
        if (data?.length) await openDetail(data[0]);
      } catch (e) {
        toast.error(apiErr(e.response?.data?.detail) || "Search failed");
      } finally { setLoading(false); }
    })();
  }, [searchParams]);

  const onSaved = (updated) => {
    setSelected((cur) => ({ ...cur, ...updated }));
    setResults((rs) => rs?.map((r) => (r.id === updated.id ? { ...r, ...updated } : r)));
    setEditing(false);
    toast.success("Student updated.");
  };

  return (
    <Layout>
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-2">Student search</div>
        <h1 className="text-3xl font-bold text-slate-900" style={{fontFamily:"Outfit"}}>Find a student</h1>
        <p className="text-slate-500 mt-1">Search by roll number or name.</p>
      </div>

      <form onSubmit={onSearch} className="flex gap-3 mb-6 max-w-2xl" data-testid="search-form">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="e.g. 5A01 or Aarav"
            className="h-12 pl-11 rounded-full text-base"
            data-testid="search-input"
          />
        </div>
        <Button type="submit" disabled={loading} className="h-12 px-6 rounded-full bg-sky-500 hover:bg-sky-600" data-testid="search-submit-btn">
          {loading ? "Searching…" : "Search"}
        </Button>
      </form>

      {results !== null && (
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            {results.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="no-results">
                <div className="h-12 w-12 mx-auto rounded-full bg-slate-100 flex items-center justify-center mb-3">
                  <Search className="h-5 w-5 text-slate-400" />
                </div>
                <div className="text-slate-700 font-medium">No student found.</div>
                <div className="text-slate-500 text-sm mt-1">Try another roll number or name.</div>
              </div>
            ) : (
              <ul className="bg-white border border-slate-200 rounded-2xl divide-y divide-slate-100 overflow-hidden" data-testid="search-results">
                {results.map((s) => (
                  <li key={s.id}>
                    <button
                      onClick={() => openDetail(s)}
                      data-testid={`result-${s.roll_number}`}
                      className={`w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors ${selected?.id === s.id ? "bg-sky-50" : ""}`}
                    >
                      <div className="h-10 w-10 rounded-full bg-slate-100 text-slate-600 flex items-center justify-center font-medium">
                        {s.name.split(" ").map((p) => p[0]).slice(0,2).join("")}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium text-slate-900 truncate">{s.name}</div>
                        <div className="text-xs text-slate-500">Roll #{s.roll_number} · Class {s.class_name} {s.section}</div>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="lg:col-span-3">
            {selected ? (
              <StudentDetail s={selected} canEdit={canEdit} onEdit={() => setEditing(true)} onRefresh={() => openDetail(selected)} />
            ) : (
              <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center text-slate-500">
                {detailLoading ? "Loading…" : "Select a student to view full profile."}
              </div>
            )}
          </div>
        </div>
      )}

      <EditStudentDialog
        open={editing}
        onClose={() => setEditing(false)}
        student={selected}
        onSaved={onSaved}
      />
    </Layout>
  );
}

function EditStudentDialog({ open, onClose, student, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Reset the form whenever a new student opens
  if (open && student && form.__id !== student.id) {
    setForm({
      __id: student.id,
      name: student.name || "",
      gender: student.gender || "",
      dob: student.dob || "",
      parent_name: student.parent_name || "",
      parent_phone: student.parent_phone || "",
      address: student.address || "",
    });
  }

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSave = async () => {
    if (!student) return;
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        gender: form.gender,
        dob: form.dob,
        parent_name: form.parent_name,
        parent_phone: form.parent_phone,
        address: form.address,
      };
      const { data } = await api.patch(`/students/${student.id}`, payload);
      onSaved(data);
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not update student");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-lg" data-testid="edit-student-dialog">
        <DialogHeader>
          <DialogTitle style={{fontFamily:"Outfit"}}>
            Edit {student?.name || "student"}
          </DialogTitle>
          <DialogDescription>Update this student's profile details and save your changes.</DialogDescription>
        </DialogHeader>
        <div className="grid sm:grid-cols-2 gap-4 py-2">
          <Field label="Name" id="edit-name"><Input id="edit-name" value={form.name || ""} onChange={set("name")} data-testid="edit-name-input"/></Field>
          <Field label="Gender" id="edit-gender"><Input id="edit-gender" value={form.gender || ""} onChange={set("gender")} placeholder="male / female / other" data-testid="edit-gender-input"/></Field>
          <Field label="Date of birth" id="edit-dob"><DatePicker value={form.dob || ""} onChange={(v) => setForm((f) => ({ ...f, dob: v }))} data-testid="edit-dob-input"/></Field>
          <Field label="Parent name" id="edit-parent-name"><Input id="edit-parent-name" value={form.parent_name || ""} onChange={set("parent_name")} data-testid="edit-parent-name-input"/></Field>
          <Field label="Parent phone" id="edit-parent-phone" full>
            <Input id="edit-parent-phone" value={form.parent_phone || ""} onChange={set("parent_phone")} placeholder="e.g. 9876543210" data-testid="edit-parent-phone-input"/>
          </Field>
          <Field label="Address" id="edit-address" full>
            <Input id="edit-address" value={form.address || ""} onChange={set("address")} data-testid="edit-address-input"/>
          </Field>
        </div>
        <div className="text-xs text-slate-500 pt-1">
          Roll number, class and section can&apos;t be edited here — that would break attendance history. Use Admin → Import to move a student.
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="edit-cancel-btn">Cancel</Button>
          <Button onClick={onSave} disabled={saving} className="bg-sky-500 hover:bg-sky-600" data-testid="edit-save-btn">
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, id, children, full }) {
  return (
    <div className={full ? "sm:col-span-2 space-y-1.5" : "space-y-1.5"}>
      <Label htmlFor={id} className="text-xs uppercase tracking-[0.15em] text-slate-500">{label}</Label>
      {children}
    </div>
  );
}

function StudentDetail({ s, canEdit, onEdit, onRefresh }) {
  const [retryingId, setRetryingId] = useState(null);
  const [resettingDevice, setResettingDevice] = useState(false);
  const handleRetry = async (c, key) => {
    setRetryingId(key);
    try {
      await api.post("/announcements/calls", {
        student_ids: [s.id],
        message: c.announcement_text,
        category: c.category || undefined,
      });
      toast.success("Call re-queued to parent");
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Retry failed");
    } finally {
      setRetryingId(null);
    }
  };
  const handleResetDevice = async () => {
    setResettingDevice(true);
    try {
      await api.post(`/students/${s.id}/reset-device`);
      toast.success("Device reset. The student can now sign in from a new device.");
      onRefresh?.();
    } catch (e) {
      toast.error(apiErr(e.response?.data?.detail) || "Could not reset the device");
    } finally {
      setResettingDevice(false);
    }
  };
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="student-detail">
      <div className="p-6 border-b border-slate-100 flex items-center gap-4">
        <div className="h-16 w-16 rounded-2xl bg-sky-50 text-sky-700 flex items-center justify-center text-xl font-semibold">
          {s.name.split(" ").map((p) => p[0]).slice(0,2).join("")}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xl font-bold text-slate-900 truncate" style={{fontFamily:"Outfit"}}>{s.name}</div>
          <div className="text-sm text-slate-500">Roll #{s.roll_number} · Class {s.class_name} Section {s.section}</div>
        </div>
        {canEdit && (
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            data-testid="edit-student-btn"
            className="rounded-full"
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" /> Edit
          </Button>
        )}
      </div>
      <div className="p-6 grid sm:grid-cols-2 gap-4">
        <DetailItem icon={User} label="Gender" value={s.gender} />
        <DetailItem icon={Calendar} label="Date of birth" value={s.dob} />
        <DetailItem icon={User} label="Parent" value={s.parent_name} />
        <DetailItem icon={Phone} label="Parent phone" value={s.parent_phone} />
        <DetailItem icon={GraduationCap} label="Class teacher" value={s.class_teacher?.name} />
        <DetailItem icon={MapPin} label="Address" value={s.address} full />
      </div>

      <div className="px-6 pb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Attendance summary</div>
        <div className="grid grid-cols-4 gap-3">
          <SumTile label="Days" value={s.attendance_summary.total_days} tone="slate" />
          <SumTile label="Present" value={s.attendance_summary.present} tone="emerald" />
          <SumTile label="Absent" value={s.attendance_summary.absent} tone="rose" />
          <SumTile label="%" value={`${s.attendance_summary.percentage}%`} tone="sky" />
        </div>
      </div>

      <div className="px-6 pb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Registered device</div>
        <div className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-4 py-3" data-testid="student-device-panel">
          <div className="flex items-center gap-3 min-w-0">
            <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${s.device_id ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
              <Smartphone className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              {s.device_id ? (
                <>
                  <div className="text-sm font-medium text-slate-800 truncate" data-testid="student-device-label">
                    {s.device_label || "Registered device"}
                  </div>
                  <div className="text-xs text-slate-500">
                    Locked to this device{s.device_bound_at ? ` · since ${new Date(s.device_bound_at).toLocaleDateString()}` : ""}
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-500">No device registered yet — first sign-in will lock the account to that device.</div>
              )}
            </div>
          </div>
          {canEdit && s.device_id && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleResetDevice}
              disabled={resettingDevice}
              className="rounded-full shrink-0 text-rose-600 border-rose-200 hover:bg-rose-50"
              data-testid="reset-device-btn"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> {resettingDevice ? "Resetting…" : "Reset device"}
            </Button>
          )}
        </div>
      </div>

      <div className="px-6 pb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Recent history</div>
        {s.recent_history.length === 0 ? (
          <div className="text-sm text-slate-500">No attendance recorded yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl">
            {s.recent_history.map((h) => (
              <li key={h.id} className="flex items-center justify-between px-4 py-2 text-sm">
                <span className="font-mono text-slate-700">{h.date}</span>
                {h.status === "present" ? (
                  <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full text-xs">
                    <CheckCircle2 className="h-3.5 w-3.5" /> Present
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-200 px-2 py-0.5 rounded-full text-xs">
                    <XCircle className="h-3.5 w-3.5" /> Absent
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="px-6 pb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-3">Parent call history</div>
        {(!s.parent_calls || s.parent_calls.length === 0) ? (
          <div className="text-sm text-slate-500" data-testid="parent-calls-empty">No calls placed to this parent yet.</div>
        ) : (
          <ul className="divide-y divide-slate-100 border border-slate-200 rounded-xl" data-testid="parent-calls-list">
            {s.parent_calls.map((c, i) => {
              const meta = callMeta(c);
              const notConnected = (c.delivery && c.delivery !== "delivered") || c.status === "failed" || c.status === "skipped";
              const canRetry = canEdit && c.kind === "announcement" && c.announcement_text && notConnected;
              return (
                <li key={i} className={`flex items-start gap-3 px-4 py-2.5 ${c.auto_retry ? "pl-8 bg-slate-50/60" : ""}`} data-testid="parent-call-row">
                  <span className={`mt-0.5 h-7 w-7 shrink-0 rounded-full flex items-center justify-center ${meta.bg}`}>
                    <meta.Icon className={`h-3.5 w-3.5 ${meta.fg}`} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-sm font-medium ${meta.fg}`}>{meta.label}</span>
                      {c.auto_retry && (
                        <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full border border-violet-200 bg-violet-50 text-violet-700 inline-flex items-center gap-1" data-testid="auto-retry-badge">
                          <RotateCcw className="h-2.5 w-2.5" /> Auto-retry
                        </span>
                      )}
                      <CallStatusBadge status={c.status} />
                      <CallOutcome call={c} />
                    </div>
                    {c.auto_retry ? (
                      <div className="text-[11px] text-violet-500 mt-0.5">↳ System re-tried the earlier unanswered call automatically.</div>
                    ) : c.announcement_text ? (
                      <div className="text-[12px] text-slate-500 mt-0.5 line-clamp-2">{c.announcement_text}</div>
                    ) : null}
                    <div className="text-[11px] text-slate-400 mt-0.5">
                      {fmtDateTime(c.created_at)}{c.triggered_by_name ? ` · by ${c.triggered_by_name}` : ""}
                    </div>
                  </div>
                  {canRetry && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-8 px-2.5 rounded-full shrink-0"
                      disabled={retryingId === i}
                      onClick={() => handleRetry(c, i)}
                      data-testid={`retry-call-${i}`}
                      title="Re-place this call"
                    >
                      <RotateCcw className={`h-3.5 w-3.5 mr-1 ${retryingId === i ? "animate-spin" : ""}`} /> Retry
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function callMeta(c) {
  if (c.category === "congrats") return { label: "Congratulated parent", Icon: Rocket, bg: "bg-emerald-50", fg: "text-emerald-600" };
  if (c.category === "concern") return { label: "Decline notice", Icon: AlertTriangle, bg: "bg-rose-50", fg: "text-rose-600" };
  if (c.kind === "absent") return { label: `Absence call${c.session ? ` (${c.session})` : ""}`, Icon: Phone, bg: "bg-amber-50", fg: "text-amber-600" };
  if (c.kind === "birthday") return { label: "Birthday wishes", Icon: Cake, bg: "bg-pink-50", fg: "text-pink-600" };
  return { label: "Announcement", Icon: Megaphone, bg: "bg-sky-50", fg: "text-sky-600" };
}

function CallStatusBadge({ status }) {
  const map = {
    success: "text-emerald-700 bg-emerald-50 border-emerald-200",
    failed: "text-rose-700 bg-rose-50 border-rose-200",
    skipped: "text-slate-600 bg-slate-50 border-slate-200",
    pending: "text-amber-700 bg-amber-50 border-amber-200",
  };
  const cls = map[status] || map.pending;
  return <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded-full border ${cls}`}>{status || "pending"}</span>;
}

function fmtDuration(ms) {
  const s = Math.round((Number(ms) || 0) / 1000);
  if (s <= 0) return null;
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}

function CallOutcome({ call }) {
  const dur = fmtDuration(call.duration_ms);
  const d = call.delivery;
  let label = null, cls = "text-slate-500 bg-slate-50 border-slate-200";
  if (d === "delivered") { label = "Picked up"; cls = "text-emerald-700 bg-emerald-50 border-emerald-200"; }
  else if (d === "no_answer") { label = "No answer"; cls = "text-amber-700 bg-amber-50 border-amber-200"; }
  else if (d === "voicemail") { label = "Voicemail"; cls = "text-sky-700 bg-sky-50 border-sky-200"; }
  else if (d === "busy") { label = "Busy"; cls = "text-amber-700 bg-amber-50 border-amber-200"; }
  else if (d === "failed") { label = "Not connected"; cls = "text-rose-700 bg-rose-50 border-rose-200"; }
  else if (d) { label = d; }
  if (!label && !dur) return null;
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded-full border inline-flex items-center gap-1 ${cls}`}>
      {label}{label && dur ? " · " : ""}{dur || ""}
    </span>
  );
}

function fmtDateTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function DetailItem({ icon: Icon, label, value, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-xs uppercase tracking-[0.2em] text-slate-400 mb-1 flex items-center gap-1.5">
        <Icon className="h-3.5 w-3.5" /> {label}
      </div>
      <div className="text-sm text-slate-900">{value || "—"}</div>
    </div>
  );
}

function SumTile({ label, value, tone }) {
  const map = {
    slate: "bg-slate-50 text-slate-700",
    emerald: "bg-emerald-50 text-emerald-700",
    rose: "bg-rose-50 text-rose-700",
    sky: "bg-sky-50 text-sky-700",
  };
  return (
    <div className={`rounded-xl px-3 py-3 ${map[tone]}`}>
      <div className="text-xs uppercase tracking-[0.15em] opacity-70">{label}</div>
      <div className="text-xl font-bold mt-0.5" style={{fontFamily:"Outfit"}}>{value}</div>
    </div>
  );
}
